/**
 * Mesh Hardware Page
 *
 * Guide to wireless dongles and DIY devices for non-cellular mesh communication.
 */

import { Link } from 'react-router-dom'
import { useState } from 'react'
import { clsx } from 'clsx'
import { InfoCard } from '../components/common'

type HardwareSection = 'overview' | 'technologies' | 'dongles' | 'diy' | 'integration' | 'experimental'

export function MeshHardwarePage() {
  const [activeSection, setActiveSection] = useState<HardwareSection>('overview')

  return (
    <div className="flex-1 flex flex-col relative overflow-hidden font-body">
      {/* Header */}
      <header className="flex items-center gap-4 p-4 border-b border-white/10 glass-panel z-10">
        <Link
          to="/"
          className="text-white/60 hover:text-primary transition-colors text-sm font-mono tracking-wider"
        >
          [ BACK ]
        </Link>
        <h1 className="text-lg font-display font-bold uppercase tracking-widest text-primary neon-glow">Mesh Hardware</h1>
      </header>

      {/* Tab navigation */}
      <nav className="flex border-b border-white/10 overflow-x-auto bg-black/20 pb-1">
        {[
          { id: 'overview', label: '📡 Why', fullLabel: 'WHY MESH' },
          { id: 'technologies', label: '📊 Compare', fullLabel: 'COMPARISON' },
          { id: 'dongles', label: '🔌 Dongles', fullLabel: 'DONGLES' },
          { id: 'diy', label: '🔧 DIY', fullLabel: 'BUILD YOUR OWN' },
          { id: 'integration', label: '🔗 Setup', fullLabel: 'INTEGRATION' },
          { id: 'experimental', label: '🧪 Direct', fullLabel: 'DIRECT INTEGRATION' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id as HardwareSection)}
            className={clsx(
              'px-6 py-4 text-xs font-bold whitespace-nowrap transition-colors uppercase tracking-widest font-mono',
              activeSection === tab.id
                ? 'text-primary border-b-2 border-primary bg-primary/5'
                : 'text-white/40 hover:text-white hover:bg-white/5'
            )}
          >
            <span className="md:hidden">{tab.label}</span>
            <span className="hidden md:inline">{tab.fullLabel}</span>
          </button>
        ))}
      </nav>

      {/* Content */}
      <main className="flex-1 p-4 overflow-y-auto space-y-6 scrollbar-hide">
        {activeSection === 'overview' && <OverviewSection />}
        {activeSection === 'technologies' && <TechnologiesSection />}
        {activeSection === 'dongles' && <DonglesSection />}
        {activeSection === 'diy' && <DIYSection />}
        {activeSection === 'integration' && <IntegrationSection />}
        {activeSection === 'experimental' && <ExperimentalSection />}

        <div className="pt-8 pb-4 text-center">
          <p className="text-[10px] font-mono text-white/20 uppercase tracking-[0.3em]">
            // End of Document //
          </p>
        </div>
      </main>
    </div>
  )
}

function OverviewSection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <InfoCard title="Why Non-Cellular?" variant="info">
        <p className="mb-4">
          CrowdTalkie works best with resilient communication that doesn't depend on
          cellular infrastructure. External mesh hardware extends your range and reliability.
        </p>
        <ul className="space-y-2 text-xs font-mono text-white/70">
          <li>• <strong className="text-red-400">Cell towers</strong> can be overloaded or unavailable</li>
          <li>• <strong className="text-green-400">Mesh networks</strong> have no single point of failure</li>
          <li>• <strong className="text-blue-400">Multiple frequencies</strong> are harder to disrupt</li>
          <li>• <strong className="text-yellow-400">Consumer hardware</strong> is legal and affordable</li>
        </ul>
      </InfoCard>

      <InfoCard title="Jamming Resistance">
        <p className="mb-4 text-sm text-white/70">
          Different wireless technologies have varying levels of resilience to interference:
        </p>
        <div className="space-y-3">
          <ResistanceBar label="LoRa" stars={5} color="green" reason="Chirp spread spectrum, sub-GHz" />
          <ResistanceBar label="Bluetooth 5.0" stars={3} color="yellow" reason="Fast frequency hopping" />
          <ResistanceBar label="Zigbee" stars={3} color="yellow" reason="DSSS + mesh healing" />
          <ResistanceBar label="WiFi" stars={2} color="red" reason="Fixed channels" />
        </div>
      </InfoCard>

      <InfoCard title="What Makes Jamming Hard?" variant="info">
        <ul className="space-y-3 text-xs font-mono text-white/70">
          <li className="flex items-start gap-2">
            <span className="text-primary">1.</span>
            <span><strong className="text-white">Spread Spectrum:</strong> Signal hops across frequencies or spreads across bandwidth</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">2.</span>
            <span><strong className="text-white">Sub-GHz Frequencies:</strong> Better propagation, requires more powerful equipment to block</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">3.</span>
            <span><strong className="text-white">Low Power / Below Noise:</strong> LoRa can decode signals 20dB below noise floor</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">4.</span>
            <span><strong className="text-white">Mesh Topology:</strong> Routes around localized interference automatically</span>
          </li>
        </ul>
      </InfoCard>
    </div>
  )
}

function TechnologiesSection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <InfoCard title="LoRa (Long Range Radio)" variant="info">
        <div className="grid grid-cols-2 gap-3 mb-4 text-xs font-mono">
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Range</div>
            <div className="text-green-400 font-bold">2-15 km</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Frequency</div>
            <div className="text-blue-400 font-bold">868/915 MHz</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Jamming Resistance</div>
            <div className="text-green-400 font-bold">HIGH</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Battery Life</div>
            <div className="text-green-400 font-bold">Days</div>
          </div>
        </div>
        <p className="text-sm text-white/70">
          Best choice for resilient mesh. Uses chirp spread spectrum (CSS) modulation
          that can decode signals even below the noise floor. Runs the Meshtastic protocol.
        </p>
      </InfoCard>

      <InfoCard title="Bluetooth (2.4 GHz)">
        <div className="grid grid-cols-2 gap-3 mb-4 text-xs font-mono">
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Range</div>
            <div className="text-yellow-400 font-bold">10-100m</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Frequency</div>
            <div className="text-blue-400 font-bold">2.4 GHz</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Jamming Resistance</div>
            <div className="text-yellow-400 font-bold">MODERATE</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Battery Life</div>
            <div className="text-green-400 font-bold">Days</div>
          </div>
        </div>
        <p className="text-sm text-white/70">
          Built into most devices. Uses frequency hopping (FHSS) across 79 channels
          at 1,600 hops/second. Already supported in CrowdTalkie as a fallback.
        </p>
      </InfoCard>

      <InfoCard title="WiFi Direct (2.4/5 GHz)">
        <div className="grid grid-cols-2 gap-3 mb-4 text-xs font-mono">
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Range</div>
            <div className="text-yellow-400 font-bold">50-200m</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Frequency</div>
            <div className="text-blue-400 font-bold">2.4/5 GHz</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Jamming Resistance</div>
            <div className="text-red-400 font-bold">LOW</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Battery Life</div>
            <div className="text-yellow-400 font-bold">Hours</div>
          </div>
        </div>
        <p className="text-sm text-white/70">
          Good for temporary base stations. Fixed channels make it easier to target.
          5 GHz less crowded but shorter range.
        </p>
      </InfoCard>

      <InfoCard title="Zigbee (2.4 GHz)">
        <div className="grid grid-cols-2 gap-3 mb-4 text-xs font-mono">
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Range</div>
            <div className="text-yellow-400 font-bold">10-100m</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Frequency</div>
            <div className="text-blue-400 font-bold">2.4 GHz</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Jamming Resistance</div>
            <div className="text-yellow-400 font-bold">MODERATE</div>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-white/40 mb-1">Battery Life</div>
            <div className="text-green-400 font-bold">Years</div>
          </div>
        </div>
        <p className="text-sm text-white/70">
          Self-healing mesh with DSSS modulation. Primarily designed for IoT but
          capable of basic messaging. Very low power consumption.
        </p>
      </InfoCard>
    </div>
  )
}

function DonglesSection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <InfoCard title="Best Overall: LoRa Devices" variant="info">
        <p className="text-sm text-white/70 mb-4">
          Recommended for maximum range and jamming resistance. Run Meshtastic firmware.
        </p>
        <div className="space-y-3">
          <DongleItem
            name="LILYGO T-Beam Supreme"
            price="$45"
            features={['LoRa 915MHz', 'GPS built-in', 'Meshtastic ready', '2-10km range']}
            recommended
          />
          <DongleItem
            name="Heltec LoRa 32 V3"
            price="$35"
            features={['LoRa + WiFi + BLE', 'OLED display', 'USB-C', 'Ready to use']}
          />
          <DongleItem
            name="RAK Wireless WisMesh"
            price="$30"
            features={['Solar compatible', 'Weatherproof', 'Long battery', 'Drop-in node']}
          />
        </div>
      </InfoCard>

      <InfoCard title="Bluetooth Extenders">
        <p className="text-sm text-white/70 mb-4">
          Extend built-in Bluetooth range. Good for phone-to-phone mesh.
        </p>
        <div className="space-y-3">
          <DongleItem
            name="Avantree DG80"
            price="$40"
            features={['Class 1 Long Range', '~30m reliable', 'Low latency', 'USB adapter']}
            recommended
          />
          <DongleItem
            name="TP-Link UB500"
            price="$13"
            features={['Bluetooth 5.0', '40m range', 'Compact', 'Budget option']}
          />
          <DongleItem
            name="ASUS USB-BT500"
            price="$15"
            features={['Bluetooth 5.0', 'Linux support', 'BLE capable', 'Small form']}
          />
        </div>
      </InfoCard>

      <InfoCard title="WiFi Adapters">
        <p className="text-sm text-white/70 mb-4">
          Create local hotspots or bridge to LoRa gateways. High power options for base stations.
        </p>
        <div className="space-y-3">
          <DongleItem
            name="Alfa AWUS036ACH"
            price="$50"
            features={['High power', 'Dual band', 'External antenna', 'Long range']}
            recommended
          />
          <DongleItem
            name="Panda PAU09"
            price="$20"
            features={['Dual band', 'Good Linux support', 'Compact', 'Reliable']}
          />
        </div>
      </InfoCard>

      <InfoCard title="Zigbee Coordinators" variant="warning">
        <p className="text-sm text-white/70 mb-4">
          Primarily for IoT mesh. Can be repurposed for simple messaging networks.
        </p>
        <div className="space-y-3">
          <DongleItem
            name="SONOFF Zigbee 3.0"
            price="$10"
            features={['CC2652P chip', 'USB stick', 'Mesh coordinator', 'Very cheap']}
          />
          <DongleItem
            name="ConBee II"
            price="$40"
            features={['Universal support', 'Web interface', 'Strong mesh', 'Well documented']}
          />
        </div>
      </InfoCard>
    </div>
  )
}

function DIYSection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <InfoCard title="Build Your Own Mesh Radio" variant="info">
        <p className="mb-4">
          A custom device optimized for CrowdTalkie with dual-radio capability:
          LoRa for long-range mesh and BLE for phone connection.
        </p>
        <div className="p-4 rounded-xl bg-black/30 border border-primary/30">
          <div className="text-center mb-2">
            <span className="text-3xl">~$36</span>
            <span className="text-white/40 text-sm ml-2">total cost</span>
          </div>
          <div className="text-center text-xs text-white/50">
            24-48 hour battery life
          </div>
        </div>
      </InfoCard>

      <InfoCard title="Parts List">
        <div className="space-y-2">
          <PartItem name="ESP32-S3 DevKit" cost="$8" purpose="Main processor + BLE" />
          <PartItem name="SX1262 LoRa Module" cost="$12" purpose="Long-range mesh radio" />
          <PartItem name='0.96" OLED (SSD1306)' cost="$4" purpose="Status display" />
          <PartItem name="18650 Battery (3000mAh)" cost="$5" purpose="24+ hour runtime" />
          <PartItem name="TP4056 USB-C Charger" cost="$2" purpose="Charging circuit" />
          <PartItem name="915MHz Whip Antenna" cost="$3" purpose="LoRa antenna" />
          <PartItem name="3D Printed Enclosure" cost="$2" purpose="Weather protection" />
        </div>
        <div className="mt-4 pt-4 border-t border-white/10 flex justify-between text-sm font-bold">
          <span className="text-white/70">Total</span>
          <span className="text-primary">~$36</span>
        </div>
      </InfoCard>

      <InfoCard title="Wiring Diagram">
        <pre className="text-[10px] font-mono text-white/70 overflow-x-auto p-4 bg-black/40 rounded-lg border border-white/10">
{`┌─────────────────────────────────────────┐
│                                         │
│  ┌─────────┐      ┌─────────┐          │
│  │ ESP32-S3│◄────►│ SX1262  │◄── Antenna
│  │         │ SPI  │ LoRa    │          │
│  │  BLE    │      └─────────┘          │
│  │  WiFi   │                           │
│  └────┬────┘                           │
│       │ I2C                            │
│  ┌────┴────┐      ┌─────────┐          │
│  │  OLED   │      │ TP4056  │◄── USB-C │
│  │ Display │      │ Charger │          │
│  └─────────┘      └────┬────┘          │
│                        │               │
│                   ┌────┴────┐          │
│                   │ 18650   │          │
│                   │ Battery │          │
│                   └─────────┘          │
└─────────────────────────────────────────┘`}
        </pre>
      </InfoCard>

      <InfoCard title="SPI Connections (ESP32-S3 → SX1262)">
        <div className="grid grid-cols-2 gap-2 text-xs font-mono">
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <span className="text-white/40">MOSI:</span> <span className="text-green-400">GPIO 11</span>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <span className="text-white/40">MISO:</span> <span className="text-green-400">GPIO 13</span>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <span className="text-white/40">SCK:</span> <span className="text-green-400">GPIO 12</span>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <span className="text-white/40">NSS:</span> <span className="text-green-400">GPIO 10</span>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <span className="text-white/40">DIO1:</span> <span className="text-green-400">GPIO 14</span>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <span className="text-white/40">BUSY:</span> <span className="text-green-400">GPIO 15</span>
          </div>
        </div>
      </InfoCard>

      <InfoCard title="Build Steps">
        <ol className="list-decimal list-inside space-y-3 text-xs font-mono text-white/70">
          <li className="pl-2">
            <strong className="text-white">Solder LoRa Module</strong>
            <p className="text-white/50 ml-5 mt-1">Connect SX1262 to ESP32-S3 via SPI pins</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Wire OLED Display</strong>
            <p className="text-white/50 ml-5 mt-1">Connect via I2C (SDA → GPIO 8, SCL → GPIO 9)</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Add Power Circuit</strong>
            <p className="text-white/50 ml-5 mt-1">TP4056 → Battery → 3.3V regulator → ESP32</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Flash Firmware</strong>
            <p className="text-white/50 ml-5 mt-1">Install PlatformIO, flash Meshtastic</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Configure Region</strong>
            <p className="text-white/50 ml-5 mt-1">Set 915MHz (US) or 868MHz (EU)</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Pair with Phone</strong>
            <p className="text-white/50 ml-5 mt-1">Connect via Bluetooth in Meshtastic app</p>
          </li>
        </ol>
      </InfoCard>

      <InfoCard title="Pre-Built Alternative" variant="warning">
        <p className="text-sm text-white/70 mb-4">
          Don't want to solder? Buy a ready-made device:
        </p>
        <div className="p-4 rounded-xl bg-black/30 border border-yellow-500/30">
          <div className="font-bold text-yellow-400 mb-2">Heltec LoRa 32 V3 ($35)</div>
          <ul className="text-xs font-mono text-white/60 space-y-1">
            <li>• Ready to use out of box</li>
            <li>• Same LoRa + BLE capability</li>
            <li>• Just flash Meshtastic firmware</li>
            <li>• Add any USB power bank</li>
          </ul>
        </div>
      </InfoCard>
    </div>
  )
}

function IntegrationSection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Important Bluetooth requirement notice */}
      <InfoCard title="How Phone-to-Radio Connection Works" variant="warning">
        <p className="mb-4 text-sm">
          <strong className="text-yellow-400">Important:</strong> Your phone connects to the LoRa radio device via <strong className="text-blue-400">Bluetooth (BLE)</strong>.
          The LoRa radio then communicates with other radios over long-range LoRa frequencies.
        </p>
        <div className="p-3 rounded bg-black/30 border border-white/10 mb-4">
          <div className="text-xs font-mono text-white/70 text-center">
            <span className="text-primary">📱 Phone</span>
            <span className="text-blue-400 mx-2">──BLE──▶</span>
            <span className="text-purple-400">📻 LoRa Radio</span>
            <span className="text-green-400 mx-2">──LoRa──▶</span>
            <span className="text-purple-400">📻 Other Radios</span>
          </div>
        </div>
        <p className="text-xs text-white/50">
          This means your phone needs Bluetooth capability AND a browser that supports the Web Bluetooth API.
          The LoRa radio handles the long-range mesh communication.
        </p>
      </InfoCard>

      {/* Web Bluetooth Compatibility */}
      <InfoCard title="Web Bluetooth Browser Support" variant="info">
        <p className="mb-4 text-sm text-white/70">
          To connect your phone to a LoRa device (like Meshtastic), you need a browser that supports the <strong className="text-primary">Web Bluetooth API</strong>:
        </p>
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-3 p-2 rounded bg-green-500/10 border border-green-500/30">
            <span className="text-green-400 text-lg">✓</span>
            <div className="flex-1">
              <div className="text-xs font-bold text-green-400">Chrome / Edge / Opera (Android)</div>
              <div className="text-[10px] text-white/50">Full support - Recommended</div>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 rounded bg-green-500/10 border border-green-500/30">
            <span className="text-green-400 text-lg">✓</span>
            <div className="flex-1">
              <div className="text-xs font-bold text-green-400">Chrome / Edge (Windows, macOS, Linux)</div>
              <div className="text-[10px] text-white/50">Full support on desktop</div>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 rounded bg-yellow-500/10 border border-yellow-500/30">
            <span className="text-yellow-400 text-lg">⚠</span>
            <div className="flex-1">
              <div className="text-xs font-bold text-yellow-400">Bluefy Browser (iOS)</div>
              <div className="text-[10px] text-white/50">Third-party browser with Web Bluetooth - works but requires separate app</div>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 rounded bg-red-500/10 border border-red-500/30">
            <span className="text-red-400 text-lg">✗</span>
            <div className="flex-1">
              <div className="text-xs font-bold text-red-400">Safari (iOS / macOS)</div>
              <div className="text-[10px] text-white/50">No Web Bluetooth support - use native Meshtastic app instead</div>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 rounded bg-red-500/10 border border-red-500/30">
            <span className="text-red-400 text-lg">✗</span>
            <div className="flex-1">
              <div className="text-xs font-bold text-red-400">Firefox (all platforms)</div>
              <div className="text-[10px] text-white/50">No Web Bluetooth support</div>
            </div>
          </div>
        </div>
        <div className="p-3 rounded bg-blue-500/10 border border-blue-500/30">
          <div className="text-xs font-bold text-blue-400 mb-1">iOS Users: Alternative Workflow</div>
          <p className="text-[10px] text-white/50">
            Use the native <strong>Meshtastic app</strong> from the App Store to connect your phone to the LoRa radio.
            The Meshtastic app handles the Bluetooth connection, and you can coordinate with others using the Meshtastic channel.
          </p>
        </div>
      </InfoCard>

      <InfoCard title="How It Works with CrowdTalkie" variant="info">
        <pre className="text-[10px] font-mono text-white/70 overflow-x-auto p-4 bg-black/40 rounded-lg border border-white/10">
{`┌─────────────────┐     ┌─────────────────┐
│   Smartphone    │     │   Smartphone    │
│   CrowdTalkie   │     │   CrowdTalkie   │
│      App        │     │      App        │
└────────┬────────┘     └────────┬────────┘
         │ BLE                   │ BLE
         ▼                       ▼
┌─────────────────┐     ┌─────────────────┐
│   Mesh Radio    │◄───►│   Mesh Radio    │
│    (LoRa)       │LoRa │    (LoRa)       │
└─────────────────┘     └─────────────────┘
         ▲                       ▲
         │ LoRa                  │ LoRa
         ▼                       ▼
┌─────────────────────────────────────────┐
│          LoRa Mesh Network              │
│    (other radios in the area)           │
└─────────────────────────────────────────┘`}
        </pre>
      </InfoCard>

      <InfoCard title="Setup Steps">
        <ol className="list-decimal list-inside space-y-3 text-xs font-mono text-white/70">
          <li className="pl-2">
            <strong className="text-white">Get a LoRa Device</strong>
            <p className="text-white/50 ml-5 mt-1">Buy pre-built (Heltec V3) or build your own</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Flash Meshtastic</strong>
            <p className="text-white/50 ml-5 mt-1">Download firmware from meshtastic.org</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Install Meshtastic App</strong>
            <p className="text-white/50 ml-5 mt-1">Available on iOS and Android</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Pair via Bluetooth</strong>
            <p className="text-white/50 ml-5 mt-1">Connect your phone to the radio</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Configure Channel</strong>
            <p className="text-white/50 ml-5 mt-1">Set encryption key and channel name</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Coordinate with Group</strong>
            <p className="text-white/50 ml-5 mt-1">Share channel settings with others</p>
          </li>
        </ol>
      </InfoCard>

      <InfoCard title="CrowdTalkie Connection Layers">
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded bg-black/20 border border-white/5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.8)]" />
              <span className="text-xs font-bold text-green-500 tracking-wider">1. P2P</span>
            </div>
            <span className="text-xs text-white/50 font-mono">Direct WebRTC when possible</span>
          </div>

          <div className="flex items-center gap-3 p-3 rounded bg-black/20 border border-white/5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-yellow-500 shadow-[0_0_5px_rgba(234,179,8,0.8)]" />
              <span className="text-xs font-bold text-yellow-500 tracking-wider">2. RELAY</span>
            </div>
            <span className="text-xs text-white/50 font-mono">WebSocket through relay nodes</span>
          </div>

          <div className="flex items-center gap-3 p-3 rounded bg-black/20 border border-white/5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_5px_rgba(59,130,246,0.8)]" />
              <span className="text-xs font-bold text-blue-500 tracking-wider">3. BT</span>
            </div>
            <span className="text-xs text-white/50 font-mono">Bluetooth mesh fallback</span>
          </div>

          <div className="flex items-center gap-3 p-3 rounded bg-purple-500/10 border border-purple-500/30">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-purple-500 shadow-[0_0_5px_rgba(168,85,247,0.8)]" />
              <span className="text-xs font-bold text-purple-500 tracking-wider">4. LoRa</span>
            </div>
            <span className="text-xs text-white/50 font-mono">External radio mesh (km range)</span>
          </div>
        </div>
      </InfoCard>

      <InfoCard title="Built-in Bluetooth vs. LoRa Bluetooth" variant="warning">
        <p className="mb-4 text-sm text-white/70">
          CrowdTalkie uses Bluetooth in <strong className="text-blue-400">two different ways</strong>:
        </p>
        <div className="space-y-3">
          <div className="p-3 rounded bg-blue-500/10 border border-blue-500/30">
            <div className="text-xs font-bold text-blue-400 mb-2">Built-in Bluetooth Mesh (Layer 3)</div>
            <ul className="text-[10px] text-white/50 space-y-1">
              <li>• Phone-to-phone direct connection</li>
              <li>• Range: ~10-30 meters</li>
              <li>• No extra hardware needed</li>
              <li>• Requires Web Bluetooth API support in browser</li>
              <li>• Works as fallback when internet fails</li>
            </ul>
          </div>
          <div className="p-3 rounded bg-purple-500/10 border border-purple-500/30">
            <div className="text-xs font-bold text-purple-400 mb-2">LoRa via Bluetooth (Layer 4)</div>
            <ul className="text-[10px] text-white/50 space-y-1">
              <li>• Phone connects to external LoRa radio via BLE</li>
              <li>• LoRa radio talks to other LoRa radios (km range)</li>
              <li>• Requires LoRa hardware device (Meshtastic)</li>
              <li>• Also requires Web Bluetooth API support</li>
              <li>• Best for long-range, jam-resistant communication</li>
            </ul>
          </div>
        </div>
        <p className="mt-4 text-[10px] text-white/40">
          Both features require Web Bluetooth support. Android Chrome works best. iOS users need
          the native Meshtastic app for LoRa, or Bluefy browser for built-in Bluetooth.
        </p>
      </InfoCard>

      <InfoCard title="Legal Notes" variant="warning">
        <ul className="space-y-2 text-xs font-mono text-white/70">
          <li>• <strong className="text-green-400">USA:</strong> LoRa 915MHz legal under FCC Part 15 (≤1W)</li>
          <li>• <strong className="text-green-400">EU:</strong> LoRa 868MHz legal under ETSI (duty cycle limits apply)</li>
          <li>• <strong className="text-green-400">Bluetooth:</strong> Legal worldwide with certified hardware</li>
          <li>• <strong className="text-yellow-400">DIY:</strong> Legal for personal use with certified radio modules</li>
          <li>• <strong className="text-red-400">Note:</strong> Don't modify antennas beyond manufacturer specs</li>
        </ul>
      </InfoCard>

      <InfoCard title="Resources">
        <div className="space-y-3">
          <a
            href="https://meshtastic.org"
            target="_blank"
            rel="noopener noreferrer"
            className="block p-3 rounded bg-black/20 border border-white/10 hover:border-primary/50 transition-colors"
          >
            <div className="font-bold text-primary text-sm">Meshtastic Project</div>
            <div className="text-xs text-white/50">meshtastic.org - firmware & guides</div>
          </a>
          <a
            href="https://lora-alliance.org"
            target="_blank"
            rel="noopener noreferrer"
            className="block p-3 rounded bg-black/20 border border-white/10 hover:border-primary/50 transition-colors"
          >
            <div className="font-bold text-primary text-sm">LoRa Alliance</div>
            <div className="text-xs text-white/50">lora-alliance.org - specifications</div>
          </a>
        </div>
      </InfoCard>
    </div>
  )
}

function ExperimentalSection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Warning Banner */}
      <div className="p-4 rounded-xl bg-yellow-500/10 border-2 border-yellow-500/30">
        <div className="flex items-start gap-3">
          <span className="text-2xl">🧪</span>
          <div>
            <h3 className="font-display font-bold text-yellow-400 mb-2">
              EXPERIMENTAL: Direct Device Integration
            </h3>
            <p className="text-sm text-white/70 mb-3">
              CrowdTalkie can now connect directly to Meshtastic and MeshCore devices via Web Bluetooth.
              This feature is under active development and testing.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 font-mono">
                BETA
              </span>
              <span className="text-[10px] px-2 py-1 rounded bg-blue-500/20 text-blue-400 font-mono">
                Meshtastic BLE
              </span>
              <span className="text-[10px] px-2 py-1 rounded bg-green-500/20 text-green-400 font-mono">
                MeshCore NUS
              </span>
            </div>
          </div>
        </div>
      </div>

      <InfoCard title="What's New" variant="info">
        <p className="mb-4 text-sm text-white/70">
          CrowdTalkie now supports <strong className="text-purple-400">direct BLE connection</strong> to
          LoRa mesh devices. No separate Meshtastic app required for basic functionality.
        </p>
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2 rounded bg-black/20 border border-white/5">
            <span className="w-2 h-2 rounded-full bg-purple-500" />
            <span className="text-xs text-white/70">Connect to Meshtastic devices directly from CrowdTalkie</span>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-black/20 border border-white/5">
            <span className="w-2 h-2 rounded-full bg-purple-500" />
            <span className="text-xs text-white/70">Bridge room messages to LoRa mesh automatically</span>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-black/20 border border-white/5">
            <span className="w-2 h-2 rounded-full bg-purple-500" />
            <span className="text-xs text-white/70">Support for both Meshtastic and MeshCore protocols</span>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-black/20 border border-white/5">
            <span className="w-2 h-2 rounded-full bg-purple-500" />
            <span className="text-xs text-white/70">Automatic channel key derivation from room secrets</span>
          </div>
        </div>
      </InfoCard>

      <InfoCard title="Supported Devices">
        <p className="mb-4 text-sm text-white/70">
          Any device running <strong className="text-blue-400">Meshtastic firmware 2.0+</strong> or
          <strong className="text-green-400"> MeshCore firmware</strong> with BLE enabled.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-xs font-bold text-blue-400 mb-1">Meshtastic</div>
            <ul className="text-[10px] text-white/50 space-y-0.5">
              <li>• LILYGO T-Beam</li>
              <li>• Heltec V3 / Wireless Tracker</li>
              <li>• RAK WisBlock 4631</li>
              <li>• T-Echo, T-Deck</li>
            </ul>
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/10">
            <div className="text-xs font-bold text-green-400 mb-1">MeshCore</div>
            <ul className="text-[10px] text-white/50 space-y-0.5">
              <li>• ESP32-S3 based devices</li>
              <li>• Nordic nRF52 devices</li>
              <li>• Custom builds with NUS</li>
            </ul>
          </div>
        </div>
      </InfoCard>

      <InfoCard title="How to Connect" variant="info">
        <ol className="list-decimal list-inside space-y-3 text-xs font-mono text-white/70">
          <li className="pl-2">
            <strong className="text-white">Ensure your LoRa device is powered on</strong>
            <p className="text-white/50 ml-5 mt-1">Bluetooth must be enabled on the device</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Open a CrowdTalkie room</strong>
            <p className="text-white/50 ml-5 mt-1">Create or join a room first</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Tap the LoRa status indicator</strong>
            <p className="text-white/50 ml-5 mt-1">Look for the purple 📻 icon in the room header</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Select your device from the list</strong>
            <p className="text-white/50 ml-5 mt-1">Your browser will show available Bluetooth devices</p>
          </li>
          <li className="pl-2">
            <strong className="text-white">Wait for configuration</strong>
            <p className="text-white/50 ml-5 mt-1">CrowdTalkie will sync channel settings automatically</p>
          </li>
        </ol>
      </InfoCard>

      <InfoCard title="Message Flow">
        <pre className="text-[10px] font-mono text-white/70 overflow-x-auto p-4 bg-black/40 rounded-lg border border-white/10">
{`┌─────────────────────────────────────────────────────────────────┐
│                      CrowdTalkie Room                           │
│                                                                 │
│  ┌──────────────┐   ┌──────────────┐   ┌──────────────┐        │
│  │  Yjs CRDT    │   │  Room radio  │   │ MeshCore     │        │
│  │  Document    │◄─►│  link + gw   │◄─►│ session      │        │
│  └──────────────┘   └──────────────┘   └──────────────┘        │
│                                               │                 │
└───────────────────────────────────────────────┼─────────────────┘
                                                │ Bluetooth (web or app)
                                    ┌───────────▼───────────┐
                                    │   LoRa Mesh Device    │
                                    │  (Meshtastic/MeshCore)│
                                    └───────────┬───────────┘
                                                │ LoRa Radio
                                    ┌───────────▼───────────┐
                                    │   Other Mesh Devices  │
                                    │   (2-15km range)      │
                                    └───────────────────────┘`}
        </pre>
      </InfoCard>

      <InfoCard title="Known Limitations" variant="warning">
        <ul className="space-y-2 text-xs font-mono text-white/70">
          <li className="flex items-start gap-2">
            <span className="text-yellow-400">⚠</span>
            <span><strong className="text-white">Browser Support:</strong> Only Chrome, Edge, and Opera support Web Bluetooth</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-yellow-400">⚠</span>
            <span><strong className="text-white">iOS:</strong> Requires third-party browser (Bluefy) - Safari not supported</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-yellow-400">⚠</span>
            <span><strong className="text-white">Message Size:</strong> LoRa packets limited to ~230 bytes - large messages fragmented</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-yellow-400">⚠</span>
            <span><strong className="text-white">Latency:</strong> LoRa has higher latency (1-10 seconds) than P2P connections</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-yellow-400">⚠</span>
            <span><strong className="text-white">One Device:</strong> Can only connect to one LoRa device at a time per browser tab</span>
          </li>
        </ul>
      </InfoCard>

      <InfoCard title="Reporting Issues">
        <p className="text-sm text-white/70 mb-3">
          Found a bug or have feedback? We want to hear from you!
        </p>
        <a
          href="https://github.com/craigm26/crowdtalkie/issues"
          target="_blank"
          rel="noopener noreferrer"
          className="block p-3 rounded bg-purple-500/10 border border-purple-500/30 hover:border-purple-500/50 transition-colors"
        >
          <div className="font-bold text-purple-400 text-sm">Report an Issue</div>
          <div className="text-xs text-white/50">GitHub Issues - crowdtalkie repo</div>
        </a>
      </InfoCard>

      <InfoCard title="Development Status">
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
            <span className="text-xs text-white/70">Meshtastic BLE Protocol</span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/20 text-green-400 font-mono">
              Implemented
            </span>
          </div>
          <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
            <span className="text-xs text-white/70">MeshCore BLE Protocol</span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/20 text-green-400 font-mono">
              Implemented
            </span>
          </div>
          <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
            <span className="text-xs text-white/70">Room ↔ LoRa Bridge</span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/20 text-green-400 font-mono">
              Implemented
            </span>
          </div>
          <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
            <span className="text-xs text-white/70">Channel Key Derivation</span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/20 text-green-400 font-mono">
              Implemented
            </span>
          </div>
          <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
            <span className="text-xs text-white/70">Real-world Testing</span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 font-mono">
              In Progress
            </span>
          </div>
          <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
            <span className="text-xs text-white/70">Voice Message Support</span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 font-mono">
              Planned
            </span>
          </div>
        </div>
      </InfoCard>
    </div>
  )
}

// Page-specific Components

function ResistanceBar({
  label,
  stars,
  color,
  reason
}: {
  label: string
  stars: number
  color: 'green' | 'yellow' | 'red'
  reason: string
}) {
  const colorClasses = {
    green: 'text-green-400 bg-green-500/20',
    yellow: 'text-yellow-400 bg-yellow-500/20',
    red: 'text-red-400 bg-red-500/20',
  }

  return (
    <div className="flex items-center gap-3 p-2 rounded bg-black/20 border border-white/5">
      <div className="w-24 text-xs font-bold text-white/80">{label}</div>
      <div className={clsx('px-2 py-1 rounded text-xs font-mono', colorClasses[color])}>
        {'★'.repeat(stars)}{'☆'.repeat(5 - stars)}
      </div>
      <div className="text-[10px] text-white/40 flex-1">{reason}</div>
    </div>
  )
}

function DongleItem({
  name,
  price,
  features,
  recommended = false,
}: {
  name: string
  price: string
  features: string[]
  recommended?: boolean
}) {
  return (
    <div className={clsx(
      'p-3 rounded-lg border transition-colors',
      recommended
        ? 'bg-primary/10 border-primary/30 hover:border-primary/50'
        : 'bg-black/20 border-white/10 hover:border-white/20'
    )}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-bold text-white text-sm">{name}</span>
          {recommended && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/20 text-primary font-bold uppercase tracking-wider">
              Recommended
            </span>
          )}
        </div>
        <span className="text-primary font-mono text-sm font-bold">{price}</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {features.map((feature, i) => (
          <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-white/50 font-mono">
            {feature}
          </span>
        ))}
      </div>
    </div>
  )
}

function PartItem({
  name,
  cost,
  purpose,
}: {
  name: string
  cost: string
  purpose: string
}) {
  return (
    <div className="flex items-center justify-between p-2 rounded bg-black/20 border border-white/5">
      <div>
        <div className="text-xs font-bold text-white">{name}</div>
        <div className="text-[10px] text-white/40">{purpose}</div>
      </div>
      <div className="text-xs font-mono text-primary">{cost}</div>
    </div>
  )
}
